import React, { useState, useRef, useEffect } from "react";
import "../SearchBar/SearchBar.css";
import { useNavigate } from "react-router-dom";

const SearchBar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [transactionType, setTransactionType] = useState("buy");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minBedrooms, setMinBedrooms] = useState("");
  const [maxBedrooms, setMaxBedrooms] = useState("");
  const [location, setLocation] = useState(""); // Location state
  const [hasSubmitted, setHasSubmitted] = useState(false); // Track submission status

  const searchBarRef = useRef(null);
  const filterBarRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasSubmitted) {
      setIsExpanded(false); // Reset to collapsed only when navigating back to search
    }
  }, [hasSubmitted]);

  const toggleExpand = () => {
    setIsExpanded(true);
  };

  const handleClickOutside = (event) => {
    if (
      searchBarRef.current &&
      !searchBarRef.current.contains(event.target) &&
      filterBarRef.current &&
      !filterBarRef.current.contains(event.target)
    ) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTransactionChange = (event) => {
    const selectedTransaction = event.target.value;
    setTransactionType(selectedTransaction);
    setMinPrice("");
    setMaxPrice("");
    setMinBedrooms("");
    setLocation("");
    console.log(`Transaction Type Changed: ${selectedTransaction}`);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault(); // Prevent the default page reload

    // Set the flag that form was submitted
    setHasSubmitted(true);

    const searchParams = new URLSearchParams();

    if (transactionType) {
      searchParams.append(
        "transaction_type",
        transactionType === "buy" ? 1 : 2
      );
    }
    if (minPrice) {
      searchParams.append("min_price", minPrice);
    }
    if (maxPrice) {
      searchParams.append("max_price", maxPrice);
    }
    if (minBedrooms) {
      searchParams.append("min_bedrooms", minBedrooms);
    }
    if (location) {
      searchParams.append("location", location.trim()); // Include location
    }

    // Log the constructed URLSearchParams
    console.log("Constructed search params:", searchParams.toString());

    // Navigate to search results and pass the search params
    console.log(
      "Navigating to search results with params:",
      searchParams.toString()
    );
    navigate(`/search-results?${searchParams.toString()}`);

    // Keep the search bar expanded after submitting
    setIsExpanded(true);
  };

  return (
    <div className="locationContainer">
      <div className="searchbarContainer">
        <form className="searchbar" onSubmit={handleSearchSubmit}>
          <div className="radioOptions">
            <input
              type="radio"
              id="buy"
              name="transaction"
              value="buy"
              checked={transactionType === "buy"}
              onChange={handleTransactionChange}
            />
            <label htmlFor="buy">Buy</label>

            <input
              type="radio"
              id="rent"
              name="transaction"
              value="rent"
              checked={transactionType === "rent"}
              onChange={handleTransactionChange}
            />
            <label htmlFor="rent">Rent</label>
          </div>

          {/* Location/Postcode Input */}
          <input
            type="text"
            className={`searchInput ${isExpanded ? "expanded" : ""}`}
            ref={searchBarRef}
            onClick={toggleExpand}
            placeholder="Search location e.g. N9 or Essex"
            aria-label="Location Search"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />

          <button type="submit" className="searchButton">
            Search
          </button>
        </form>

        {isExpanded && (
          <div className="filterBar" ref={filterBarRef}>
            {/* Min Price Dropdown */}
            <div className={`searchGroup ${minPrice ? "hasValue" : ""}`}>
              {minPrice && <label className="searchLabel">Min Price</label>}
              <select
                className="searchSelect"
                aria-label="Min Price"
                value={minPrice}
                onChange={(e) => {
                  setMinPrice(e.target.value);
                  console.log(`Min Price changed: ${e.target.value}`);
                }}
              >
                <option value="">Min Price</option>
                {transactionType === "buy" ? (
                  <>
                    <option value="50000">£50,000</option>
                    <option value="100000">£100,000</option>
                    <option value="150000">£150,000</option>
                    <option value="200000">£200,000</option>
                    <option value="250000">£250,000</option>
                    <option value="300000">£300,000</option>
                    <option value="350000">£350,000</option>
                    <option value="400000">£400,000</option>
                    <option value="450000">£450,000</option>
                    <option value="500000">£500,000</option>
                    <option value="550000">£550,000</option>
                    <option value="600000">£600,000</option>
                    <option value="650000">£650,000</option>
                    <option value="700000">£700,000</option>
                    <option value="800000">£800,000</option>
                    <option value="900000">£900,000</option>
                    <option value="1000000">£1,000,000</option>
                  </>
                ) : (
                  <>
                    <option value="500">£500pcm</option>
                    <option value="750">£750pcm</option>
                    <option value="1000">£1,000pcm</option>
                    <option value="1250">£1,250pcm</option>
                    <option value="1500">£1,500pcm</option>
                    <option value="1750">£1,750pcm</option>
                    <option value="2000">£2,000pcm</option>
                    <option value="2500">£2,500pcm</option>
                    <option value="3000">£3,000pcm</option>
                    <option value="3500">£3,500pcm</option>
                    <option value="4000">£4,000pcm</option>
                    <option value="5000">£5,000pcm</option>
                    <option value="7500">£7,500pcm</option>
                    <option value="10000">£10,000pcm</option>
                    <option value="50000">£50,000pcm</option>
                  </>
                )}
              </select>
            </div>

            {/* Max Price Dropdown */}
            <div className={`searchGroup ${maxPrice ? "hasValue" : ""}`}>
              {maxPrice && <label className="searchLabel">Max Price</label>}
              <select
                className="searchSelect"
                aria-label="Max Price"
                value={maxPrice}
                onChange={(e) => {
                  setMaxPrice(e.target.value);
                  console.log(`Max Price changed: ${e.target.value}`);
                }}
              >
                <option value="">Max Price</option>
                {transactionType === "buy" ? (
                  <>
                    <option value="50000">£50,000</option>
                    <option value="100000">£100,000</option>
                    <option value="150000">£150,000</option>
                    <option value="200000">£200,000</option>
                    <option value="250000">£250,000</option>
                    <option value="300000">£300,000</option>
                    <option value="350000">£350,000</option>
                    <option value="400000">£400,000</option>
                    <option value="450000">£450,000</option>
                    <option value="500000">£500,000</option>
                    <option value="550000">£550,000</option>
                    <option value="600000">£600,000</option>
                    <option value="650000">£650,000</option>
                    <option value="700000">£700,000</option>
                    <option value="800000">£800,000</option>
                    <option value="900000">£900,000</option>
                    <option value="1000000">£1,000,000</option>
                  </>
                ) : (
                  <>
                    <option value="500">£500pcm</option>
                    <option value="750">£750pcm</option>
                    <option value="1000">£1,000pcm</option>
                    <option value="1250">£1,250pcm</option>
                    <option value="1500">£1,500pcm</option>
                    <option value="1750">£1,750pcm</option>
                    <option value="2000">£2,000pcm</option>
                    <option value="2500">£2,500pcm</option>
                    <option value="3000">£3,000pcm</option>
                    <option value="3500">£3,500pcm</option>
                    <option value="4000">£4,000pcm</option>
                    <option value="5000">£5,000pcm</option>
                    <option value="7500">£7,500pcm</option>
                    <option value="10000">£10,000pcm</option>
                    <option value="50000">£50,000pcm</option>
                  </>
                )}
              </select>
            </div>

            {/* Min Bedrooms Dropdown */}
            <div className={`searchGroup ${minBedrooms ? "hasValue" : ""}`}>
              {minBedrooms && <label className="searchLabel">Bedrooms</label>}
              <select
                className="searchSelect"
                aria-label="Min Bedrooms"
                value={minBedrooms}
                onChange={(e) => {
                  setMinBedrooms(e.target.value);
                  console.log(`Min Bedrooms changed: ${e.target.value}`);
                }}
              >
                <option value="">Bedrooms</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4+</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
